import * as React from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../store/index';
import {
    FMTreeGroupData,
    FMTreeItemDataType,
    TreeNodeType,
    TreeDisplayStateType,
    TreeType,
    TreeViewType,
    GeofenceType,
} from '../../../store/FMTree/FMTreeReducer';

import { FMLoading, FMTreeRootEmptyNode } from 'molecules';

import {
    expandGroup,
    expandAll,
    collapseGroup,
    collapseAll,
    checkNode,
    checkAll,
    uncheckNode,
    uncheckAll,
    selectNode,
    enableEditMode,
    disableEditMode,
    searchNode,
    showAsTree,
    showAsList,
    deleteSelectedNode,
    deleteCheckedOrSelectedItems,
    exportCheckedOrSelectedItems,
    addItemsToStore,
    fetchGeotreeNodeData,
    fetchGeotreeNewNodeData,
    importItems,
    expandParentsAndSelectNode,
} from '../../../store/FMTree/FMTreeActions';
import { getRootNode, isTreeFetching, getViewType, getAllItemsIds } from '../../../store/FMTree/FMTReeSelectors';

interface OwnProps {
    treeType: TreeType;
    treeDisplayState: TreeDisplayStateType;
    groupNode: any;
    itemNode: any;
    showRoot?: boolean;
}

interface StateProps {
    rootNode: FMTreeGroupData;
    isFetching: boolean;
    viewType: TreeViewType;
    allItemsIds: string[];
}

interface DispatchProps {
    expandGroup: (treeDisplayState: TreeDisplayStateType, id: string) => void;
    expandAll: (treeType: TreeType, treeDisplayState: TreeDisplayStateType) => void;
    collapseGroup: (treeDisplayState: TreeDisplayStateType, id: string) => void;
    collapseAll: (treeDisplayState: TreeDisplayStateType) => void;
    checkNode: (treeType: TreeType, treeDisplayState: TreeDisplayStateType, nodeType: TreeNodeType, id: string) => void;
    checkAll: (treeType: TreeType, treeDisplayState: TreeDisplayStateType) => void;
    uncheckNode: (treeType: TreeType, treeDisplayState: TreeDisplayStateType, nodeType: TreeNodeType, id: string) => void;
    uncheckAll: (treeDisplayState: TreeDisplayStateType) => void;
    selectNode: (treeDisplayState: TreeDisplayStateType, nodeType: TreeNodeType, id: string) => void;
    enableEditMode: (treeDisplayState: TreeDisplayStateType) => void;
    disableEditMode: (treeDisplayState: TreeDisplayStateType) => void;
    searchNode: (tree: TreeType, displayState: TreeDisplayStateType, text: string) => void;
    showAsTree: (displayState: TreeDisplayStateType) => void;
    showAsList: (displayState: TreeDisplayStateType) => void;
    deleteSelectedNode: (tree: TreeType, displayState: TreeDisplayStateType) => void;
    deleteCheckedOrSelectedItems: (tree: TreeType, displayState: TreeDisplayStateType) => void;
    exportCheckedOrSelectedItems: (displayState: TreeDisplayStateType, fileType: string) => void;
    addItemsToStore: (tree: TreeType, treeDisplayState: TreeDisplayStateType, items: FMTreeItemDataType[], parentId: string) => void;
    fetchGeotreeNodeData: (forEdit: boolean) => void;
    fetchGeotreeNewNodeData: (nodeType: TreeNodeType, geofenceType: GeofenceType) => void;
    importItems: (tree: TreeType, treeDisplayState: TreeDisplayStateType, content: string, parentId: string) => void;
    expandParentsAndSelectNode: (treeType: TreeType, treeDisplayState: TreeDisplayStateType, nodeType: TreeNodeType, id: string) => void;
}

type Props = OwnProps & StateProps & DispatchProps;

class FMTree extends React.Component<Props> {

    constructor(props) {
        super(props);
    }

    private renderLoading = () => <FMLoading />;

    public expandAllGroups = () => {
        this.props.expandAll(this.props.treeType, this.props.treeDisplayState);
    }

    public collapseAllGroups = () => {
        this.props.collapseAll(this.props.treeDisplayState);
    }

    public checkAllNodes = () => {
        this.props.checkAll(this.props.treeType, this.props.treeDisplayState);
    }

    public uncheckAllNodes = () => {
        this.props.uncheckAll(this.props.treeDisplayState);
    }

    public enableEditMode = () => {
        this.props.enableEditMode(this.props.treeDisplayState);
    }

    public disableEditMode = () => {
        this.props.disableEditMode(this.props.treeDisplayState);
    }

    public selectNode = (nodeType: TreeNodeType, nodeId: string) => {
        this.props.selectNode(this.props.treeDisplayState, nodeType, nodeId);
    }

    public selectNodeAndExpandParents = (
        displayState: TreeDisplayStateType,
        nodeType: TreeNodeType,
        nodeId: string,
    ) => {
        this.props.expandParentsAndSelectNode(this.props.treeType, this.props.treeDisplayState, nodeType, nodeId);
    }

    public searchNode = (text: string) => {
        this.props.searchNode(this.props.treeType, this.props.treeDisplayState, text);
    }

    public showAsTree = () => {
        this.props.showAsTree(this.props.treeDisplayState);
    }

    public showAsList = () => {
        this.props.showAsList(this.props.treeDisplayState);
    }

    public deleteSelectedNode = () => {
        this.props.deleteSelectedNode(this.props.treeType, this.props.treeDisplayState);
    }

    public deleteCheckedOrSelectedItems = () => {
        this.props.deleteCheckedOrSelectedItems(this.props.treeType, this.props.treeDisplayState);
    }

    public exportCheckedOrSelectedItems = (fileType: string) => {
        this.props.exportCheckedOrSelectedItems(this.props.treeDisplayState, fileType);
    }

    public addItemsToStore = (items: FMTreeItemDataType[], parentId: string) => {
        this.props.addItemsToStore(this.props.treeType, this.props.treeDisplayState, items, parentId);
    }

    public fetchSelectedNodeEditData = () => {
        this.props.fetchGeotreeNodeData(true);
    }

    public fetchGeotreeNewNodeData = (nodeType: TreeNodeType, geofenceType?: GeofenceType) => {
        this.props.fetchGeotreeNewNodeData(nodeType, geofenceType);
    }

    public importItems = (content: string, parentId: string) =>
        this.props.importItems(this.props.treeType, this.props.treeDisplayState, content, parentId)

    public render() {

        if (this.props.isFetching) {
            return this.renderLoading();
        }

        if (this.props.rootNode === undefined) {
            return null;
        }

        const GroupNode = this.props.groupNode;
        const ItemNode = this.props.itemNode;

        if (this.props.viewType === TreeViewType.list) {
            return (
                this.props.allItemsIds.map(id =>
                    <ItemNode
                        key={id}
                        id={id}
                        level={0}
                        treeType={this.props.treeType}
                        treeDisplayState={this.props.treeDisplayState}
                        checkNodeBase={this.props.checkNode}
                        uncheckNodeBase={this.props.uncheckNode}
                        selectNodeBase={this.selectNodeAndExpandParents}
                    />
                )
            );
        }

        return (
            <React.Fragment>
                {
                    this.props.showRoot &&
                        <FMTreeRootEmptyNode
                            key={'0'}
                            id={'0'}
                            level={0}
                            treeType={this.props.treeType}
                            treeDisplayState={this.props.treeDisplayState}
                            expandGroupBase={this.props.expandGroup}
                            collapseGroupBase={this.props.collapseGroup}
                            checkNodeBase={this.props.checkNode}
                            uncheckNodeBase={this.props.uncheckNode}
                            selectNodeBase={this.props.selectNode}
                            itemComponent={this.props.itemNode}
                        />
                }
                {
                    this.props.rootNode && this.props.rootNode.childGroups.map(id =>
                        <GroupNode
                            key={id}
                            id={id}
                            level={0}
                            treeType={this.props.treeType}
                            treeDisplayState={this.props.treeDisplayState}
                            expandGroupBase={this.props.expandGroup}
                            collapseGroupBase={this.props.collapseGroup}
                            checkNodeBase={this.props.checkNode}
                            uncheckNodeBase={this.props.uncheckNode}
                            selectNodeBase={this.props.selectNode}
                            itemComponent={this.props.itemNode}
                        />
                    )
                }
            </React.Fragment>
        );
    }

}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
    rootNode: getRootNode(state, ownProps.treeType),
    isFetching: isTreeFetching(state, ownProps.treeType),
    viewType: getViewType(state, ownProps.treeDisplayState),
    allItemsIds: getAllItemsIds(state, ownProps.treeType),
});

const mapDispatchToProps = {
    expandGroup,
    expandAll,
    collapseGroup,
    collapseAll,
    checkNode,
    checkAll,
    uncheckNode,
    uncheckAll,
    selectNode,
    searchNode,
    showAsTree,
    showAsList,
    deleteSelectedNode,
    deleteCheckedOrSelectedItems,
    exportCheckedOrSelectedItems,
    addItemsToStore,
    fetchGeotreeNodeData,
    fetchGeotreeNewNodeData,
    enableEditMode,
    disableEditMode,
    importItems,
    expandParentsAndSelectNode,
};

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps, { forwardRef: true })(FMTree);
